import * as yup from "yup";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useLayoutEffect, useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Radio from "@mui/material/Radio";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import FormLabel from "@mui/material/FormLabel";
import RadioGroup from "@mui/material/RadioGroup";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import Autocomplete from "@mui/material/Autocomplete";
import FormHelperText from "@mui/material/FormHelperText";
import FormControlLabel from "@mui/material/FormControlLabel";
import CircularProgress from "@mui/material/CircularProgress";
import * as api from "../../../../shared/lib/api";
import { LoadingPage } from "../../../../shared/components/loading-page";
import { useCore } from "../../../../shared/hooks/use-core";
import { ApiProps, ListAllApiProps } from "../../../../features/api/types/api";
import { extractApiErrorMessage } from "../../../../shared/utils/extract-api-error-message";
import {
	ResponseCategory,
	ResponseClassificationProps,
	ResponseKeyType,
	ResponseStatus,
	categoryLabel,
	keyTypeLabel,
	statusColor,
	statusLabel,
} from "../../../../features/product/types/response-classification";

const LIST_PATH = "/report-templates?tab=parametrizacao";
const REQUIRED = "Campo obrigatório";
const TEXT_KEY_TYPE_WARNING =
	"Para tipo Texto, a categoria não muda o bloco do extrato; a regra só traduz o valor.";
const DUPLICATE_RULE_MESSAGE =
	"Já existe uma regra para esta API com o mesmo campo de origem, tipo de chave e retorno do fornecedor.";

const KEY_TYPES = Object.keys(keyTypeLabel) as ResponseKeyType[];
const CATEGORIES = Object.keys(categoryLabel) as ResponseCategory[];
const STATUSES = Object.keys(statusLabel) as ResponseStatus[];

interface Values {
	api: string;
	source_field: string;
	key_type: ResponseKeyType | "";
	key: string;
	label: string;
	category: ResponseCategory | "";
	status: ResponseStatus | "";
}

const initialValues: Values = {
	api: "",
	source_field: "",
	key_type: "",
	key: "",
	label: "",
	category: "",
	status: "",
};

const saveErrorMessage = (response: { [key: string]: any }) => {
	const message = extractApiErrorMessage(response) ?? "";
	return /set único/i.test(message) ? DUPLICATE_RULE_MESSAGE : message;
};

const toFormValues = (rule: ResponseClassificationProps): Values => ({
	api: rule.api,
	source_field: rule.source_field,
	key_type: rule.key_type,
	key: rule.key,
	label: rule.label ?? "",
	category: rule.category,
	status: rule.status,
});

const toPayload = (formValues: Values) => ({
	api: formValues.api,
	source_field: formValues.source_field.trim(),
	key_type: formValues.key_type,
	key: formValues.key.trim(),
	label: formValues.label.trim(),
	category: formValues.category,
	status: formValues.status,
});

const validationSchema = yup.object({
	api: yup.string().required(REQUIRED),
	source_field: yup.string().trim().required(REQUIRED),
	key_type: yup.string().required(REQUIRED),
	key: yup.string().trim().required(REQUIRED),
	category: yup.string().required(REQUIRED),
	status: yup.string().required(REQUIRED),
});

function ColorDot({ color, selected }: { color: string; selected?: boolean }) {
	return (
		<Box
			sx={{
				width: 20,
				height: 20,
				borderRadius: "50%",
				backgroundColor: color,
				boxShadow: selected ? `0 0 0 2px #ffffff, 0 0 0 4px ${color}` : "none",
			}}
		/>
	);
}

export default function CreateOrUpdateResponseClassification(): JSX.Element {
	const { id } = useParams();
	const navigate = useNavigate();
	const { setTitleBar, setPathTitleBar } = useCore();

	const [apis, setApis] = useState<ApiProps[]>([]);
	const [saving, setSaving] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(!!id);
	const [original, setOriginal] = useState<Values | null>(null);
	const [updatedAt, setUpdatedAt] = useState<string>("");

	const {
		values,
		errors,
		touched,
		handleChange,
		handleSubmit,
		setFieldValue,
		resetForm,
	} = useFormik({
		initialValues,
		validationSchema,
		onSubmit: (formValues: Values) =>
			id
				? updateResponseClassification(formValues)
				: createResponseClassification(formValues),
	});

	async function createResponseClassification(formValues: Values) {
		const { label, ...payload } = toPayload(formValues);
		const data = label ? { ...payload, label } : payload;

		setSaving(true);
		const response = (await api.post(
			"report/response-classification/create/",
			data,
		)) as { [key: string]: any };
		setSaving(false);

		if (response?.status === 201) {
			toast.success("Parametrização cadastrada com sucesso!", {
				position: toast.POSITION.TOP_RIGHT,
			});
			navigate(LIST_PATH);
		} else {
			toast.error(saveErrorMessage(response), {
				position: toast.POSITION.TOP_RIGHT,
			});
		}
	}

	async function updateResponseClassification(formValues: Values) {
		const current = toPayload(formValues);
		const previous = original ? toPayload(original) : null;
		const data = Object.fromEntries(
			Object.entries(current).filter(
				([field, value]) => previous?.[field as keyof typeof current] !== value,
			),
		);

		if (Object.keys(data).length === 0) {
			toast.info("Nenhuma alteração para salvar.", {
				position: toast.POSITION.TOP_RIGHT,
			});
			return;
		}

		setSaving(true);
		const response = (await api.put(
			`report/response-classification/update/${id}/`,
			data,
		)) as { [key: string]: any };
		setSaving(false);

		if (response?.status === 200) {
			toast.success("Parametrização atualizada com sucesso!", {
				position: toast.POSITION.TOP_RIGHT,
			});
			navigate(LIST_PATH);
		} else {
			toast.error(saveErrorMessage(response), {
				position: toast.POSITION.TOP_RIGHT,
			});
		}
	}

	const getResponseClassification = async () => {
		try {
			const response = (await api.get(
				`report/response-classification/retrieve/${id}/`,
			)) as { content?: ResponseClassificationProps };
			if (!response?.content) throw new Error("Parametrização não encontrada.");
			if (!response.content.is_active) {
				toast.error("Regra inativa não pode ser editada.", {
					position: toast.POSITION.TOP_RIGHT,
				});
				navigate(LIST_PATH);
				return;
			}
			const formValues = toFormValues(response.content);
			resetForm({ values: formValues });
			setOriginal(formValues);
			setUpdatedAt(response.content.updated_at);
			setLoading(false);
		} catch {
			toast.error("Não foi possível carregar a parametrização.", {
				position: toast.POSITION.TOP_RIGHT,
			});
			navigate(LIST_PATH);
		}
	};

	const fieldError = (field: keyof Values) =>
		touched[field] && errors[field] ? errors[field] : undefined;

	useEffect(() => {
		api
			.get("api/list/all/")
			.then((response) => setApis((response as ListAllApiProps)?.content ?? []))
			.catch(() =>
				toast.error("Não foi possível carregar as APIs.", {
					position: toast.POSITION.TOP_RIGHT,
				}),
			);
		if (id) getResponseClassification();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);

	useLayoutEffect(() => {
		setTitleBar(
			id
				? "Editar parametrização de retorno"
				: "Nova parametrização de retorno",
		);
		setPathTitleBar(undefined);
	}, [id, setTitleBar, setPathTitleBar]);

	if (loading) return <LoadingPage />;

	return (
		<Box component="form" noValidate onSubmit={handleSubmit} sx={{ p: 2 }}>
			<Typography variant="h6">Dados da regra</Typography>
			{id && updatedAt && (
				<Typography
					variant="caption"
					color="text.secondary"
					sx={{ display: "block" }}
				>
					Modificado em {updatedAt}
				</Typography>
			)}
			<Box sx={{ mb: 2 }} />

			<Grid container spacing={2}>
				<Grid item xs={12} md={6}>
					<Autocomplete
						options={apis}
						getOptionLabel={(option) => option.name}
						isOptionEqualToValue={(option, value) => option.id === value.id}
						value={apis.find((item) => item.id === values.api) ?? null}
						onChange={(_event, option) =>
							setFieldValue("api", option?.id ?? "")
						}
						renderInput={(params) => (
							<TextField
								{...params}
								required
								label="API"
								error={!!fieldError("api")}
								helperText={fieldError("api")}
							/>
						)}
					/>
				</Grid>

				<Grid item xs={12} md={6}>
					<TextField
						fullWidth
						required
						id="source_field"
						name="source_field"
						label="Campo de origem"
						placeholder="ex.: restricoes"
						value={values.source_field}
						onChange={handleChange}
						error={!!fieldError("source_field")}
						helperText={
							fieldError("source_field") ??
							"Nome do campo no retorno da API que contém o valor."
						}
					/>
				</Grid>

				<Grid item xs={12} md={6}>
					<FormControl required error={!!fieldError("key_type")}>
						<FormLabel id="key-type-label">Tipo de chave</FormLabel>
						<RadioGroup
							row
							aria-labelledby="key-type-label"
							name="key_type"
							value={values.key_type}
							onChange={handleChange}
						>
							{KEY_TYPES.map((keyType) => (
								<FormControlLabel
									key={keyType}
									value={keyType}
									control={<Radio />}
									label={keyTypeLabel[keyType]}
								/>
							))}
						</RadioGroup>
						{fieldError("key_type") && (
							<FormHelperText>{fieldError("key_type")}</FormHelperText>
						)}
					</FormControl>
				</Grid>

				<Grid item xs={12} md={6}>
					<TextField
						fullWidth
						required
						id="key"
						name="key"
						label="Retorno do fornecedor"
						placeholder="ex.: 66"
						value={values.key}
						onChange={handleChange}
						error={!!fieldError("key")}
						helperText={
							fieldError("key") ??
							"Código ou texto exatamente como a API devolve."
						}
					/>
				</Grid>

				<Grid item xs={12}>
					<TextField
						fullWidth
						id="label"
						name="label"
						label="Texto personalizado"
						value={values.label}
						onChange={handleChange}
						helperText="Texto exibido ao cliente no extrato. Se vazio, o extrato mostra o retorno do fornecedor."
					/>
				</Grid>

				<Grid item xs={12} md={6}>
					<FormControl required error={!!fieldError("category")}>
						<FormLabel id="category-label">Categoria</FormLabel>
						<RadioGroup
							row
							aria-labelledby="category-label"
							name="category"
							value={values.category}
							onChange={handleChange}
						>
							{CATEGORIES.map((category) => (
								<FormControlLabel
									key={category}
									value={category}
									control={<Radio />}
									label={categoryLabel[category]}
								/>
							))}
						</RadioGroup>
						{fieldError("category") && (
							<FormHelperText>{fieldError("category")}</FormHelperText>
						)}
						{values.key_type === "text" && (
							<FormHelperText sx={{ color: "text.secondary" }}>
								{TEXT_KEY_TYPE_WARNING}
							</FormHelperText>
						)}
					</FormControl>
				</Grid>

				<Grid item xs={12} md={6}>
					<FormControl required error={!!fieldError("status")}>
						<FormLabel id="status-label">Cor de alerta</FormLabel>
						<RadioGroup
							row
							aria-labelledby="status-label"
							name="status"
							value={values.status}
							onChange={handleChange}
						>
							{STATUSES.map((status) => (
								<FormControlLabel
									key={status}
									value={status}
									label={statusLabel[status]}
									sx={{ "& .MuiFormControlLabel-label": { ml: 0.5 } }}
									control={
										<Radio
											icon={<ColorDot color={statusColor[status]} />}
											checkedIcon={
												<ColorDot color={statusColor[status]} selected />
											}
										/>
									}
								/>
							))}
						</RadioGroup>
						{fieldError("status") && (
							<FormHelperText>{fieldError("status")}</FormHelperText>
						)}
					</FormControl>
				</Grid>
			</Grid>

			<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 4 }}>
				<Button
					variant="outlined"
					onClick={() => navigate(LIST_PATH)}
					disabled={saving}
				>
					Cancelar
				</Button>
				<Button type="submit" variant="contained" disabled={saving}>
					{saving ? <CircularProgress size={22} color="inherit" /> : "Salvar"}
				</Button>
			</Box>
		</Box>
	);
}
